import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useTheme } from '../../contexts/ThemeContext';
import { newsAPI } from '../../services/api';
import { getStorageUrl } from '../../utils/imageHelpers';
import Card from '../../components/UI/Card';
import Input from '../../components/UI/Input';
import PublicHeader from '../../components/Layout/PublicHeader';
import PublicFooter from '../../components/Layout/PublicFooter';
import { 
  Calendar, 
  User, 
  Camera,
  Search
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const PageContainer = styled.div`
  min-height: 100vh;
  background-color: ${props => props.theme.colors.background.primary};
  display: flex;
  flex-direction: column;
`;

const PageHeader = styled.div`
  background-color: ${props => props.theme.colors.white};
  padding: 3rem 0 2rem 0;
  margin-bottom: 2rem;
  box-shadow: ${props => props.theme.shadows.sm};
`;

const HeaderContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
`;

const Title = styled.h1`
  font-size: ${props => props.theme.typography.fontSize['3xl']};
  font-weight: ${props => props.theme.typography.fontWeight.bold};
  color: ${props => props.theme.colors.text.primary};
  font-family: ${props => props.theme.typography.fontFamily.heading};
  margin: 0 0 0.5rem 0;
`;

const Subtitle = styled.p`
  font-size: ${props => props.theme.typography.fontSize.lg};
  color: ${props => props.theme.colors.text.secondary};
  margin: 0;
`;

const Content = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem 4rem 2rem;
  flex: 1;
  width: 100%;
`;

const FilterSection = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 2rem;
  flex-wrap: wrap;
  align-items: center;
`;

const SearchWrapper = styled.div`
  flex: 1;
  min-width: 250px;
`;

const CategoryFilter = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
`;

const CategoryButton = styled.button`
  padding: 0.5rem 1rem;
  background-color: ${props => props.active ? props.theme.colors.primary : props.theme.colors.white};
  color: ${props => props.active ? props.theme.colors.white : props.theme.colors.text.primary};
  border: 1px solid ${props => props.theme.colors.primary};
  border-radius: ${props => props.theme.borderRadius.md};
  font-size: ${props => props.theme.typography.fontSize.sm};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background-color: ${props => props.theme.colors.primary};
    color: ${props => props.theme.colors.white};
  }
`;

const NewsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 2rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const NewsCard = styled(Card)`
  padding: 0;
  overflow: hidden;
  transition: transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out;
  
  &:hover {
    transform: translateY(-4px);
    box-shadow: ${props => props.theme.shadows.xl};
  }
`;

const NewsImage = styled.div`
  width: 100%;
  height: 250px;
  background-color: ${props => props.theme.colors.background.secondary};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${props => props.theme.colors.text.secondary};
  position: relative;
  overflow: hidden;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const NewsContent = styled.div`
  padding: 1.5rem;
`;

const CategoryBadge = styled.span`
  display: inline-block;
  padding: 0.25rem 0.75rem;
  background-color: ${props => props.theme.colors.primary}20;
  color: ${props => props.theme.colors.primary};
  border-radius: ${props => props.theme.borderRadius.full};
  font-size: ${props => props.theme.typography.fontSize.xs};
  font-weight: ${props => props.theme.typography.fontWeight.semibold};
  text-transform: uppercase;
  margin-bottom: 0.75rem;
`;

const NewsTitle = styled.h3`
  font-size: ${props => props.theme.typography.fontSize.xl};
  font-weight: ${props => props.theme.typography.fontWeight.semibold};
  color: ${props => props.theme.colors.text.primary};
  font-family: ${props => props.theme.typography.fontFamily.heading};
  margin: 0 0 0.75rem 0;
  line-height: 1.4;
`;

const NewsMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
  font-size: ${props => props.theme.typography.fontSize.sm};
  color: ${props => props.theme.colors.text.secondary};
  margin-bottom: 1rem;

  span {
    display: flex;
    align-items: center;
    gap: 0.25rem;
  }
`;

const NewsExcerpt = styled.p`
  font-size: ${props => props.theme.typography.fontSize.sm};
  color: ${props => props.theme.colors.text.secondary};
  font-family: ${props => props.theme.typography.fontFamily.primary};
  line-height: 1.6;
  margin: 0;
`;

const ReadMoreButton = styled.button`
  margin-top: 1.5rem;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.25rem;
  background-color: ${props => props.theme.colors.primary};
  color: ${props => props.theme.colors.white};
  border: none;
  border-radius: ${props => props.theme.borderRadius.md};
  font-size: ${props => props.theme.typography.fontSize.sm};
  font-weight: ${props => props.theme.typography.fontWeight.semibold};
  cursor: pointer;
  transition: background-color 0.2s ease, opacity 0.2s ease;

  &:hover {
    background-color: ${props => props.theme.colors.primaryDark || props.theme.colors.primary};
    opacity: 0.9;
  }
`;

const LoadingMessage = styled.div`
  text-align: center;
  padding: 4rem 2rem;
  color: ${props => props.theme.colors.text.secondary};
  font-size: ${props => props.theme.typography.fontSize.lg};
`;

const EmptyMessage = styled.div`
  text-align: center;
  padding: 4rem 2rem;
  
  h3 {
    font-size: ${props => props.theme.typography.fontSize.xl};
    color: ${props => props.theme.colors.text.primary};
    margin-bottom: 0.5rem;
  }
  
  p {
    color: ${props => props.theme.colors.text.secondary};
    font-size: ${props => props.theme.typography.fontSize.base};
  }
`;

const NewsPage = () => {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');

  const categories = [
    { value: 'all', label: 'All News' },
    { value: 'announcement', label: 'Announcements' },
    { value: 'event', label: 'Events' },
    { value: 'success_story', label: 'Success Stories' },
    { value: 'press_release', label: 'Press Releases' },
    { value: 'general', label: 'General' }
  ];

  useEffect(() => {
    fetchNews();
  }, []);

  const fetchNews = async () => {
    try {
      setLoading(true);
      const response = await newsAPI.getAll({ 
        status: 'published',
        per_page: 100  // Get all published news
      });
      
      const newsData = response.data.data || response.data;
      setNews(Array.isArray(newsData) ? newsData : []);
    } catch (error) {
      console.error('Error fetching news:', error);
      setNews([]);
    } finally {
      setLoading(false);
    }
  };

  const filteredNews = news.filter(item => {
    const matchesSearch = 
      item.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (item.summary && item.summary.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (item.content && item.content.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesCategory = selectedCategory === 'all' || item.category === selectedCategory;
    
    return matchesSearch && matchesCategory;
  });

  const handleNewsClick = (newsId) => {
    navigate(`/news/${newsId}`);
  };

  const truncateText = (text, maxLength = 160) => {
    if (!text) return '';
    return text.length > maxLength ? `${text.substring(0, maxLength).trim()}...` : text;
  };

  const formatCategory = (category) => {
    return category.replace(/_/g, ' ');
  };

  return (
    <PageContainer theme={theme}>
      <PublicHeader theme={theme} />
      
      <PageHeader theme={theme}>
        <HeaderContent>
          <Title theme={theme}>News & Updates</Title>
          <Subtitle theme={theme}>
            Stay informed with the latest news, announcements, and success stories from EWHRDN
          </Subtitle>
        </HeaderContent>
      </PageHeader>

      <Content>
        <FilterSection>
          <SearchWrapper>
            <Input
              placeholder="Search news by title, content..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              icon={<Search size={20} />}
            />
          </SearchWrapper>
          
          <CategoryFilter>
            {categories.map(category => (
              <CategoryButton
                key={category.value}
                active={selectedCategory === category.value}
                onClick={() => setSelectedCategory(category.value)}
                theme={theme}
              >
                {category.label}
              </CategoryButton>
            ))}
          </CategoryFilter>
        </FilterSection>

        {loading ? (
          <LoadingMessage theme={theme}>
            Loading news articles...
          </LoadingMessage>
        ) : filteredNews.length === 0 ? (
          <EmptyMessage theme={theme}>
            <h3>No news found</h3>
            <p>
              {searchTerm || selectedCategory !== 'all' 
                ? 'Try adjusting your search or filters' 
                : 'No news articles available at the moment'}
            </p>
          </EmptyMessage>
        ) : (
          <NewsGrid>
            {filteredNews.map((newsItem) => (
              <NewsCard 
                key={newsItem.id} 
                theme={theme}
              >
                <NewsImage theme={theme}>
                  {newsItem.featured_image_url ? (
                    <img 
                      src={getStorageUrl(newsItem.featured_image_url)} 
                      alt={newsItem.title}
                    />
                  ) : (
                    <Camera size={64} style={{ opacity: 0.3 }} />
                  )}
                </NewsImage>
                
                <NewsContent>
                  <CategoryBadge theme={theme}>
                    {formatCategory(newsItem.category)}
                  </CategoryBadge>
                  
                  <NewsTitle theme={theme}>{newsItem.title}</NewsTitle>
                  
                  <NewsMeta theme={theme}>
                    <span>
                      <Calendar size={16} />
                      {new Date(newsItem.published_date || newsItem.created_at).toLocaleDateString()}
                    </span>
                    <span>
                      <User size={16} />
                      {newsItem.author?.full_name || newsItem.author?.fullName || 'EWHRDN'}
                    </span>
                  </NewsMeta>
                  
                  <NewsExcerpt theme={theme}>
                    {truncateText(newsItem.summary, 180) || 'Summary not available.'}
                  </NewsExcerpt>

                  <ReadMoreButton
                    type="button"
                    theme={theme}
                    onClick={() => handleNewsClick(newsItem.id)}
                  >
                    Read more
                  </ReadMoreButton>
                </NewsContent>
              </NewsCard>
            ))}
          </NewsGrid>
        )}
      </Content>

      <PublicFooter theme={theme} />
    </PageContainer>
  );
};

export default NewsPage;

